import React, { useRef } from 'react';

function CreateBillModal({
  showBillModal,
  setShowBillModal,
  billFormData,
  expenseFields,
  salesFields,
  ledgerParties,
  companyLanguage,
  isSaving,
  goodsMessageUrduOptions,
  handleBillFormChange,
  handleBillCustomerChange,
  handleExpenseChange,
  handleSalesChange,
  handleExpenseKeyDown,
  handleSalesKeyDown,
  handleCreateBill,
  handleUpdateBill,
  isEdit = false,
  editingBill = null
}) {
  const submitButtonClicked = useRef(false);

  // Handle Enter key to move to next field instead of submitting
  const handleFormKeyDown = (e, nextFieldId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      if (nextFieldId) {
        // Small delay to ensure preventDefault is processed
        setTimeout(() => {
          const nextField = document.getElementById(nextFieldId);
          if (nextField) {
            nextField.focus();
          } else {
            // If no next field, try to find first expense field
            const firstExpenseInput = document.getElementById('expense-input-0');
            if (firstExpenseInput) {
              firstExpenseInput.focus();
            }
          }
        }, 10);
      }
      return false;
    }
  };

  // Always render the component structure, but conditionally show modal
  if (!showBillModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-6 max-h-[95vh] overflow-y-auto" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
        {/* Modern Centered Title */}
        <div className="text-center mb-8 pb-6 border-b border-gray-200">
          <h2 className={`text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
            {isEdit 
              ? (companyLanguage === 'ur' ? 'بل تبدیل کریں' : 'Edit Bill')
              : (companyLanguage === 'ur' ? 'نیا بل' : 'Create New Bill')
            }
          </h2>
          <p className="text-gray-600 text-sm mt-2">
            {isEdit
              ? (companyLanguage === 'ur' ? 'بل کی تفصیلات تبدیل کریں' : 'Update bill details below')
              : (companyLanguage === 'ur' ? 'بل کی تفصیلات درج کریں' : 'Enter bill details below')
            }
          </p>
        </div>
        
        {/* Close Button */}
        <button
          onClick={() => setShowBillModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          style={companyLanguage === 'ur' ? { right: 'auto', left: '1rem' } : {}}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <form 
          onSubmit={(e) => {
            e.preventDefault();
            // Only submit if submit button was actually clicked
            if (submitButtonClicked.current) {
              submitButtonClicked.current = false; // Reset flag
              if (isEdit && handleUpdateBill) {
                handleUpdateBill(e);
              } else {
                handleCreateBill(e);
              }
            }
          }}
          onKeyDown={(e) => {
            // Prevent form submission on Enter key anywhere in form
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          className="space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'سیریل نمبر' : 'Serial Number'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-serialNumber"
                type="text"
                name="serialNumber"
                value={billFormData.serialNumber}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-date')}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                placeholder={companyLanguage === 'ur' ? '600' : '600'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-date"
                type="date"
                name="date"
                value={billFormData.date}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-customerPartyId')}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'کسٹمر کا نام' : 'Customer / Supplier'} <span className="text-red-500">*</span>
              </label>
              {/* Select customer or supplier from ledger parties */}
              <select
                id="bill-customerPartyId"
                name="customerPartyId"
                value={billFormData.customerPartyId}
                onChange={handleBillCustomerChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-customerNameUrdu')}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg bg-white ${companyLanguage === 'ur' ? 'text-right' : ''}`}
              >
                <option value="">
                  {companyLanguage === 'ur' ? 'کسٹمر / سپلائر منتخب کریں' : 'Select customer or supplier'}
                </option>
                {ledgerParties.map((p) => (
                  <option key={p._id} value={p._id}>
                    {companyLanguage === 'ur'
                      ? `${p.nameUrdu || p.name} - ${p.type === 'customer' ? 'گاہک' : 'سپلائر'}`
                      : `${p.name} (${p.type === 'customer' ? 'Customer' : 'Supplier'})`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'کسٹمر کا نام (اردو)' : 'Customer Name (Urdu)'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-customerNameUrdu"
                type="text"
                name="customerNameUrdu"
                value={billFormData.customerNameUrdu || ''}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-customerName')}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                dir="rtl"
                placeholder={companyLanguage === 'ur' ? 'کسٹمر کا نام' : 'کسٹمر کا نام'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'کسٹمر کا نام (انگریزی)' : 'Customer Name (English)'}
              </label>
              <input
                id="bill-customerName"
                type="text"
                name="customerName"
                value={billFormData.customerName || ''}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-receivedMessage')}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                placeholder={companyLanguage === 'ur' ? 'Customer Name' : 'Customer Name'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'وصول شدہ پیغام (انگریزی)' : 'Received Message (English)'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-receivedMessage"
                type="text"
                name="receivedMessage"
                value={billFormData.receivedMessage}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-receivedMessageUrdu')}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={companyLanguage === 'ur' ? 'گاڑی نمبر' : 'Vehicle Number'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'وصول شدہ پیغام (اردو)' : 'Received Message (Urdu)'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-receivedMessageUrdu"
                type="text"
                name="receivedMessageUrdu"
                value={billFormData.receivedMessageUrdu}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-goodsMessage')}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                dir="rtl"
                placeholder={companyLanguage === 'ur' ? 'گاڑی نمبر' : 'Vehicle Number'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'مال کا پیغام (انگریزی)' : 'Goods Message (English)'} <span className="text-red-500">*</span>
              </label>
              <input
                id="bill-goodsMessage"
                type="text"
                name="goodsMessage"
                value={billFormData.goodsMessage}
                onChange={handleBillFormChange}
                onKeyDown={(e) => handleFormKeyDown(e, 'bill-goodsMessageUrdu')}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={companyLanguage === 'ur' ? 'آپکا مال بذریعہ بلٹی' : 'Your goods via consignment/bill of lading'}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                {companyLanguage === 'ur' ? 'مال کا پیغام (اردو)' : 'Goods Message (Urdu)'} <span className="text-red-500">*</span>
              </label>
              {/* Company will select one of the predefined Urdu options; selected value will appear on the bill */}
              <select
                id="bill-goodsMessageUrdu"
                name="goodsMessageUrdu"
                value={billFormData.goodsMessageUrdu}
                onChange={handleBillFormChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Move to first expense field if available
                    const firstExpenseInput = document.getElementById('expense-input-0');
                    if (firstExpenseInput) {
                      firstExpenseInput.focus();
                    }
                  }
                }}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg bg-white ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                dir="rtl"
              >
                <option value="" disabled>
                  {companyLanguage === 'ur' ? 'مال کا پیغام منتخب کریں' : 'Select goods message (Urdu)'}
                </option>
                {goodsMessageUrduOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Expense Fields */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className={`text-lg font-semibold text-gray-900 mb-4 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
              {companyLanguage === 'ur' ? 'خرچ' : 'Expenses'}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {(expenseFields && expenseFields.length > 0 ? expenseFields : [
                { fieldName: 'Vehicle', fieldNameUrdu: 'گاڑی', fieldType: 'number', isRequired: false, order: 0 },
                { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', isRequired: false, order: 1 },
                { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', isRequired: false, order: 2 },
                { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', isRequired: false, order: 3 },
                { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', isRequired: false, order: 4 },
                { fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ', fieldType: 'number', isRequired: false, order: 5 },
                { fieldName: 'Market Fee', fieldNameUrdu: 'مارکیٹ فیس', fieldType: 'number', isRequired: false, order: 6 }
              ]).map((field, index) => (
                <div key={index} className="flex flex-col">
                  <label className={`text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                    {/* Always show Urdu if available, otherwise show English */}
                    {(field.fieldNameUrdu && field.fieldNameUrdu.trim()) ? field.fieldNameUrdu : field.fieldName}
                  </label>
                  <input
                    id={`expense-input-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={billFormData.expenses[field.fieldName] || ''}
                    onChange={(e) => handleExpenseChange(field.fieldName, e.target.value)}
                    onKeyDown={(e) => handleExpenseKeyDown(e, index)}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                    dir="rtl"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Sales Fields */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className={`text-lg font-semibold text-gray-900 mb-4 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
              {companyLanguage === 'ur' ? 'بیچنا' : 'Sales'}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {(salesFields && salesFields.length > 0 ? salesFields : [
                { fieldName: 'Raw Sale', fieldNameUrdu: 'خام بِکری', fieldType: 'number', isRequired: true, order: 0 },
                { fieldName: 'Expenses', fieldNameUrdu: 'اخراجات', fieldType: 'number', isRequired: false, order: 1 },
                { fieldName: 'Net Sale', fieldNameUrdu: 'پختہ بِکری', fieldType: 'number', isRequired: false, order: 2 }
              ]).map((field, index) => (
                <div key={index} className="flex flex-col">
                  <label className={`text-sm font-medium text-gray-700 mb-2 ${companyLanguage === 'ur' ? 'text-right' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                    {/* Always show Urdu if available, otherwise show English */}
                    {(field.fieldNameUrdu && field.fieldNameUrdu.trim()) ? field.fieldNameUrdu : field.fieldName}
                    {field.isRequired && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    id={`sales-input-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={billFormData.sales[field.fieldName] || ''}
                    onChange={(e) => handleSalesChange(field.fieldName, e.target.value)}
                    onKeyDown={(e) => handleSalesKeyDown(e, index)}
                    required={field.isRequired}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 ${companyLanguage === 'ur' ? 'text-right' : ''}`}
                    dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={`flex gap-4 pt-4 border-t border-gray-200 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={() => setShowBillModal(false)}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
            >
              {companyLanguage === 'ur' ? 'منسوخ' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              onClick={() => {
                submitButtonClicked.current = true;
              }}
              className={`flex-1 px-6 py-3 ${isEdit ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg disabled:opacity-50 font-semibold transition flex items-center justify-center gap-2`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEdit 
                    ? (companyLanguage === 'ur' ? 'تبدیل ہو رہا ہے...' : 'Updating...')
                    : (companyLanguage === 'ur' ? 'بنا رہے ہیں...' : 'Creating...')
                  }
                </>
              ) : (
                <>
                  {isEdit ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {companyLanguage === 'ur' ? 'تبدیل کریں' : 'Update Bill'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {companyLanguage === 'ur' ? 'بنائیں' : 'Create Bill'}
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateBillModal;

